import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Search, CheckCircle, Pencil } from 'lucide-react';
import ExportButton from '../../components/ExportButton';
import PDFExportButton from '../../components/PDFExportButton';
import { toast } from 'sonner';

const ProductionEntry = () => {
  const [entries, setEntries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [machines, setMachines] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [users, setUsers] = useState([]);
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderFilter, setOrderFilter] = useState('all');
  const [editing, setEditing] = useState(null);

  const emptyForm = { production_order_id: '', batch_number: '', machine_id: '', shift_id: '', operator_id: '', supervisor_id: '', product_sku: '', product_name: '', good_qty: 0, reject_qty: 0, scrap_qty: 0, rework_qty: 0, rejection_reason: '', entry_date: new Date().toISOString().split('T')[0] };
  const [formData, setFormData] = useState({ ...emptyForm });

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    try {
      const [eRes, oRes, mRes, sRes, uRes, rRes] = await Promise.all([
        api.get('/production/entries'), api.get('/production/orders'),
        api.get('/production/machines'), api.get('/sales/shifts'), api.get('/admin/users'),
        api.get('/production/rejection-reasons')
      ]);
      setEntries(eRes.data); setOrders(oRes.data); setMachines(mRes.data);
      setShifts(sRes.data); setUsers(uRes.data); setRejectionReasons(rRes.data);
    } catch(e) { console.error(e); } finally { setLoading(false); }
  };

  const openCreate = () => { setEditing(null); setFormData({ ...emptyForm, entry_date: new Date().toISOString().split('T')[0] }); setShowModal(true); };

  const openEdit = (entry) => {
    setEditing(entry);
    setFormData({
      production_order_id: entry.production_order_id || '', batch_number: entry.batch_number || '',
      machine_id: entry.machine_id || '', shift_id: entry.shift_id || '',
      operator_id: entry.operator_id || '', supervisor_id: entry.supervisor_id || '',
      product_sku: entry.product_sku || '', product_name: entry.product_name || '',
      good_qty: entry.good_qty || 0, reject_qty: entry.reject_qty || 0, scrap_qty: entry.scrap_qty || 0, rework_qty: entry.rework_qty || 0,
      rejection_reason: entry.rejection_reason || '', entry_date: entry.entry_date || new Date().toISOString().split('T')[0]
    });
    setShowModal(true);
  };

  const onOrderSelect = (orderId) => {
    const order = orders.find(o => o.id === orderId);
    setFormData({ ...formData, production_order_id: orderId, product_sku: order?.product_sku || '', product_name: order?.product_name || '', batch_number: order?.batch_number || formData.batch_number });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/production/entries/${editing.id}`, formData);
        toast.success('Entry updated');
      } else {
        await api.post('/production/entries', formData);
        toast.success('Entry logged');
      }
      setShowModal(false); setEditing(null); fetchData();
    } catch (err) {
      const msg = err.response?.status === 403 ? 'You are not authorized to modify production entries' : (err.response?.data?.detail || 'Failed');
      toast.error(msg);
    }
  };

  const approveEntry = async (id) => {
    try {
      await api.put(`/production/entries/${id}/approve`, { remarks: '' });
      toast.success('Entry approved');
      fetchData();
    } catch (err) {
      const msg = err.response?.status === 403 ? 'You are not authorized to approve production entries' : (err.response?.data?.detail || 'Failed');
      toast.error(msg);
    }
  };

  const filtered = entries.filter(e => {
    const matchSearch = (e.product_sku || '').toLowerCase().includes(searchTerm.toLowerCase()) || (e.operator_name || '').toLowerCase().includes(searchTerm.toLowerCase()) || (e.machine_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchOrder = orderFilter === 'all' || e.production_order_id === orderFilter;
    return matchSearch && matchOrder;
  });

  const activeOrders = orders.filter(o => ['in_progress', 'released'].includes(o.status));

  return (
    <div data-testid="production-entry-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Production Entry</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-tertiary)', fontSize: '13px' }}>Execution Layer - Log shift-wise production output</p>
        </div>
        <div className="flex gap-2">
          <ExportButton endpoint="/export/production-entries" filename="production_entries_export.xlsx" />
          <PDFExportButton endpoint="/export/pdf/production-entries" filename="production_entries_export.pdf" />
          <button onClick={openCreate} className="btn btn-primary" data-testid="add-entry-btn"><Plus size={16} /> New Entry</button>
        </div>
      </div>

      <div className="card mb-4">
        <div className="flex gap-4" style={{ flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
            <input type="text" className="form-input" placeholder="Search entries..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-input" />
          </div>
          <select className="form-select" style={{ width: '240px' }} value={orderFilter} onChange={(e) => setOrderFilter(e.target.value)} data-testid="order-filter">
            <option value="all">All Orders</option>
            {orders.map(o => <option key={o.id} value={o.id}>{o.order_number} - {o.product_name}</option>)}
          </select>
        </div>
      </div>

      <div className="card">
        {loading ? <div>Loading...</div> : (
          <div className="table-container">
            <table>
              <thead><tr><th>Date</th><th>PO #</th><th>Batch #</th><th>Product</th><th>Machine</th><th>Shift</th><th>Operator</th><th>Good</th><th>Reject</th><th>Scrap</th><th>Rework</th><th>Reject Reason</th><th>Approved</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.length === 0 ? <tr><td colSpan={14} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No entries found</td></tr> : filtered.map(e => (
                  <tr key={e.id} data-testid={`entry-row-${e.id}`}>
                    <td className="font-mono">{e.entry_date}</td>
                    <td className="font-mono text-xs">{e.production_order_number || orders.find(o => o.id === e.production_order_id)?.order_number || '-'}</td>
                    <td className="font-mono text-xs">{e.batch_number || '-'}</td>
                    <td>{e.product_sku}</td>
                    <td>{e.machine_name || '-'}</td>
                    <td>{e.shift_name || '-'}</td>
                    <td>{e.operator_name || '-'}</td>
                    <td className="tabular-nums" style={{ color: '#059669', fontWeight: 600 }}>{e.good_qty}</td>
                    <td className="tabular-nums" style={{ color: '#DC2626' }}>{e.reject_qty}</td>
                    <td className="tabular-nums" style={{ color: '#D97706' }}>{e.scrap_qty}</td>
                    <td className="tabular-nums">{e.rework_qty}</td>
                    <td className="text-xs">{e.rejection_reason || '-'}</td>
                    <td>{e.approved_by_supervisor ? <span className="badge badge-success">Yes</span> : <span className="badge badge-warning">Pending</span>}</td>
                    <td>
                      <div className="flex gap-1">
                        {!e.approved_by_supervisor && (
                          <button onClick={() => openEdit(e)} className="btn btn-ghost btn-sm" title="Edit" data-testid={`edit-btn-${e.id}`}><Pencil size={14} /></button>
                        )}
                        {!e.approved_by_supervisor && (
                          <button onClick={() => approveEntry(e.id)} className="btn btn-ghost btn-sm" title="Approve" data-testid={`approve-btn-${e.id}`}><CheckCircle size={14} color="#059669" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="entry-modal">
          <div className="modal-content" style={{ maxWidth: '650px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editing ? 'Edit Production Entry' : 'New Production Entry'}</h2></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Production Order *</label>
                    <select className="form-select" value={formData.production_order_id} onChange={(e) => onOrderSelect(e.target.value)} required disabled={!!editing} data-testid="order-select">
                      <option value="">Select Order</option>
                      {(editing ? orders : activeOrders).map(o => <option key={o.id} value={o.id}>{o.order_number} - {o.product_name}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Entry Date *</label><input type="date" className="form-input" value={formData.entry_date} onChange={(e) => setFormData({...formData, entry_date: e.target.value})} required data-testid="date-input" /></div>
                </div>
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">PO Number</label>
                    <input type="text" className="form-input" value={orders.find(o => o.id === formData.production_order_id)?.order_number || ''} readOnly placeholder="Auto from order" data-testid="po-number-display" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Batch Number</label>
                    <input type="text" className="form-input" value={formData.batch_number} onChange={(e) => setFormData({...formData, batch_number: e.target.value})} placeholder="Batch No." data-testid="batch-number-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Machine</label>
                    <select className="form-select" value={formData.machine_id} onChange={(e) => setFormData({...formData, machine_id: e.target.value})} data-testid="machine-select">
                      <option value="">Select Machine</option>
                      {machines.map(m => <option key={m.id} value={m.id}>{m.name} ({m.machine_code})</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Shift</label>
                    <select className="form-select" value={formData.shift_id} onChange={(e) => setFormData({...formData, shift_id: e.target.value})} data-testid="shift-select">
                      <option value="">Select Shift</option>
                      {shifts.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Operator</label>
                    <select className="form-select" value={formData.operator_id} onChange={(e) => setFormData({...formData, operator_id: e.target.value})} data-testid="operator-select">
                      <option value="">Select Operator</option>
                      {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Supervisor</label>
                    <select className="form-select" value={formData.supervisor_id} onChange={(e) => setFormData({...formData, supervisor_id: e.target.value})} data-testid="supervisor-select">
                      <option value="">Select Supervisor</option>
                      {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-4">
                  <div className="form-group"><label className="form-label">Good Qty *</label><input type="number" className="form-input" value={formData.good_qty} onChange={(e) => setFormData({...formData, good_qty: parseInt(e.target.value) || 0})} min={0} required data-testid="good-qty-input" /></div>
                  <div className="form-group"><label className="form-label">Reject Qty</label><input type="number" className="form-input" value={formData.reject_qty} onChange={(e) => setFormData({...formData, reject_qty: parseInt(e.target.value) || 0})} min={0} data-testid="reject-qty-input" /></div>
                  <div className="form-group"><label className="form-label">Scrap Qty</label><input type="number" className="form-input" value={formData.scrap_qty} onChange={(e) => setFormData({...formData, scrap_qty: parseInt(e.target.value) || 0})} min={0} data-testid="scrap-qty-input" /></div>
                  <div className="form-group"><label className="form-label">Rework Qty</label><input type="number" className="form-input" value={formData.rework_qty} onChange={(e) => setFormData({...formData, rework_qty: parseInt(e.target.value) || 0})} min={0} data-testid="rework-qty-input" /></div>
                </div>
                {formData.reject_qty > 0 && (
                  <div className="form-group">
                    <label className="form-label">Rejection Reason (for batch {formData.batch_number || '—'}) *</label>
                    <select className="form-select" value={formData.rejection_reason} onChange={(e) => setFormData({...formData, rejection_reason: e.target.value})} required data-testid="rejection-reason-select">
                      <option value="">Select reason...</option>
                      {rejectionReasons.map(r => <option key={r.id} value={r.reason}>{r.reason} ({r.category})</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => { setShowModal(false); setEditing(null); }} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-entry-btn">{editing ? 'Update Entry' : 'Log Entry'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductionEntry;
